import { Extension } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionKeyDownProps, type SuggestionProps } from "@tiptap/suggestion";
import { forwardRef, useImperativeHandle, useState } from "react";
import { cn } from "@/lib/utils";
import { SLASH_BLOCK_TYPES, type BlockType } from "./block-types";
import { listenEscapeFirst } from "./escape-first";

const RECENT_KEY = "pylo.richtext.slash-recent";
const RECENT_LIMIT = 3;
const ROW_HEIGHT = 30;

const readRecent = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
  } catch {
    return [];
  }
};

const rememberRecent = (id: string) => {
  const next = [id, ...readRecent().filter((r) => r !== id)].slice(0, RECENT_LIMIT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
};

const matchingItems = (query: string): BlockType[] => {
  const q = query.toLowerCase();
  const matches = SLASH_BLOCK_TYPES.filter(
    (item) => item.label.toLowerCase().includes(q) || item.keywords?.some((k) => k.includes(q)),
  );
  if (q) return matches;
  const recent = readRecent();
  const rank = (item: BlockType) => {
    const i = recent.indexOf(item.id);
    return i === -1 ? RECENT_LIMIT : i;
  };
  return [...matches].sort((a, b) => rank(a) - rank(b));
};

type SlashMenuRef = { onKeyDown: (props: SuggestionKeyDownProps) => boolean };

const SlashMenu = forwardRef<SlashMenuRef, SuggestionProps<BlockType>>(
  ({ items, command }, ref) => {
    const [selected, setSelected] = useState(0);
    const [shownItems, setShownItems] = useState(items);

    if (shownItems !== items) {
      setShownItems(items);
      setSelected(0);
    }

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (!items.length) return false;
        if (event.key === "ArrowDown") {
          setSelected((s) => (s + 1) % items.length);
          return true;
        }
        if (event.key === "ArrowUp") {
          setSelected((s) => (s - 1 + items.length) % items.length);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          command(items[selected]);
          return true;
        }
        return false;
      },
    }));

    if (!items.length) {
      return <div className="px-2 py-1.5 text-sm text-muted-foreground">No matching blocks</div>;
    }

    return (
      <>
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            style={{ height: ROW_HEIGHT }}
            // Keeps focus (and the suggestion) in the editor.
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setSelected(index)}
            onClick={() => command(item)}
            className={cn(
              "flex w-full items-center gap-2 rounded px-2 text-left text-sm text-foreground",
              index === selected && "bg-muted",
            )}
          >
            <item.icon size={16} className="shrink-0 text-muted-foreground" />
            <span className="flex-1">{item.label}</span>
            {item.shortcut && (
              <kbd className="font-mono text-xs text-muted-foreground">{item.shortcut.trim()}</kbd>
            )}
          </button>
        ))}
      </>
    );
  },
);
SlashMenu.displayName = "SlashMenu";

// The menu lives inside the editor's own container rather than document.body:
// a pointerdown outside a Radix dialog's DOM would close the dialog.
const placeMenu = (popup: HTMLElement, props: SuggestionProps<BlockType>) => {
  const container = popup.parentElement;
  const caret = props.clientRect?.();
  if (!container || !caret) return;
  const box = container.getBoundingClientRect();
  const menuHeight = Math.max(props.items.length, 1) * ROW_HEIGHT + 10;
  const bounds = popup.closest('[role="dialog"]')?.getBoundingClientRect() ?? {
    top: 0,
    bottom: window.innerHeight,
  };
  const below = bounds.bottom - caret.bottom;
  const fitsBelow = below >= menuHeight || below >= caret.top - bounds.top;
  popup.style.left = `${Math.max(0, Math.min(caret.left - box.left, box.width - 256))}px`;
  if (fitsBelow) {
    popup.style.top = `${caret.bottom - box.top + 4}px`;
    popup.style.bottom = "auto";
  } else {
    popup.style.bottom = `${box.bottom - caret.top + 4}px`;
    popup.style.top = "auto";
  }
};

export const SlashCommand = Extension.create({
  name: "slashCommand",

  addProseMirrorPlugins() {
    return [
      Suggestion<BlockType>({
        editor: this.editor,
        pluginKey: new PluginKey("slashCommand"),
        char: "/",
        allow: ({ editor }) => !editor.isActive("codeBlock"),
        items: ({ query }) => matchingItems(query),
        command: ({ editor, range, props: item }) => {
          rememberRecent(item.id);
          item.apply(editor.chain().focus().deleteRange(range)).run();
        },
        render: () => {
          let renderer: ReactRenderer<SlashMenuRef, SuggestionProps<BlockType>> | null = null;
          let popup: HTMLDivElement | null = null;
          let latest: SuggestionProps<BlockType> | null = null;
          let stopEscape: (() => void) | null = null;

          return {
            onStart: (props) => {
              latest = props;
              renderer = new ReactRenderer(SlashMenu, { props, editor: props.editor });
              popup = document.createElement("div");
              popup.className = "absolute z-50 w-64 rounded-md border bg-popover p-1 shadow-md";
              popup.appendChild(renderer.element);
              props.editor.view.dom.parentElement?.appendChild(popup);
              placeMenu(popup, props);
              stopEscape = listenEscapeFirst(() => {
                if (latest) latest.editor.chain().focus().deleteRange(latest.range).run();
              });
            },
            onUpdate: (props) => {
              latest = props;
              renderer?.updateProps(props);
              if (popup) placeMenu(popup, props);
            },
            onKeyDown: (props) => renderer?.ref?.onKeyDown(props) ?? false,
            onExit: () => {
              stopEscape?.();
              popup?.remove();
              renderer?.destroy();
              popup = null;
              renderer = null;
              latest = null;
            },
          };
        },
      }),
    ];
  },
});
